import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { xtreamApi } from '../services/xtreamApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, AlertCircle, User, Lock, Tv, Server } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const LoginPage = ({ licenseValid = true }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    serverId: 0
  });
  const [servers, setServers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { appName, loginBg, colorFrom, colorTo } = useSettings();

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    xtreamApi.getServers().then(s => {
      setServers(Array.isArray(s) ? s : []);
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Always try admin login first
    try {
      const adminRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: formData.username, password: formData.password })
      });
      if (adminRes.ok) {
        localStorage.setItem('admin_creds', JSON.stringify({ username: formData.username, password: formData.password }));
        navigate('/admin', { replace: true });
        return;
      }
    } catch {}

    // Block non-admin login if no license
    if (!licenseValid) {
      setError('No valid license. Only admin login is available.');
      setIsLoading(false);
      return;
    }

    try {
      await login(formData.username, formData.password, parseInt(formData.serverId));
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to connect. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-4 relative overflow-hidden">
      {loginBg && <img src={loginBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/70 to-transparent" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Tv style={{color:"var(--accent)"}} className="w-10 h-10" />
            <h1 className="text-3xl font-bold bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(to right, ${colorFrom}, ${colorTo})` }}>
              {appName}
            </h1>
          </div>
        </div>

        <div className="bg-[#141417] rounded-2xl p-8 shadow-2xl border border-gray-800">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Server selector (only if multiple servers) */}
            {servers.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="serverId" className="text-gray-300 flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Server
                </Label>
                <select
                  id="serverId"
                  name="serverId"
                  data-testid="login-server-select"
                  value={formData.serverId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-[#1a1a1d] border border-gray-700 rounded-md text-white text-sm focus:outline-none"
                >
                  {servers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                Username
              </Label>
              <Input
                data-testid="login-username"
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                placeholder="Your username"
                className="bg-[#1a1a1d] border-gray-700 text-white"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <Input
                data-testid="login-password"
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Your password"
                className="bg-[#1a1a1d] border-gray-700 text-white"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={isLoading}
              className="w-full hover:opacity-90 text-white py-6 text-base font-medium"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-gray-500 text-sm">
            Enter your credentials to access your account
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
